/**
 * REDUX STORE — Core + plugin reducers.
 * Chiamare DOPO loadPlugins() e registerCoreEntityTypes().
 */

import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { coreReducers } from './coreReducers';
import { getPluginReducers } from '../plugin/registry';

export function createAppStore() {
  const pluginReducers = getPluginReducers();

  // A parità di chiave lo spread fa vincere il plugin. Non è un rischio
  // lasciato aperto: `registerPlugin` rifiuta già un manifest che dichiari una
  // slice con un nome del core (CORE_SLICE_NAMES), quindi qui non ci si può
  // arrivare senza che il boot sia già fallito con un messaggio esplicito.
  const rootReducer = combineReducers({
    ...coreReducers,
    ...pluginReducers,
  });

  return configureStore({ reducer: rootReducer });
}

export type AppStore = ReturnType<typeof createAppStore>;

/**
 * Shape del RootState core tipizzato. I plugin aggiungono slice dinamici:
 * l'intersezione con `Record<string, unknown>` mantiene il boundary aperto
 * senza perdere il typing statico dei core slice. Risultato pratico:
 * `state.persons` è tipato `PersonState`, `state['plugin_x']` è `unknown`.
 *
 * Derivato da `coreReducers` invece di essere riscritto a mano: era la stessa
 * lista di ventiquattro chiavi ripetuta due volte nello stesso file, e nulla
 * costringeva le due copie a restare d'accordo.
 */
type CoreRootState = {
  [K in keyof typeof coreReducers]: ReturnType<(typeof coreReducers)[K]>;
};

export type RootState = CoreRootState & Record<string, unknown>;
export type AppDispatch = AppStore['dispatch'];
